<script>
import ModalRoleComponent from '../../features/modals/ModalRoleComponent';

export default {
  name: 'ModalRoleView',
  extends: ModalRoleComponent,
  computed: {
    isUpdate() {
      return this.formData?.state === 'update' || Boolean(this.formData?.id);
    },
    modalTitle() {
      return this.isUpdate ? 'Actualizar Rol' : 'Registrar Rol';
    },
    modalSubtitle() {
      return this.isUpdate
        ? 'Gestiona el acceso y privilegios específicos asignados a este rol en la plataforma'
        : 'Define el nombre y los permisos asociados para el nuevo rol de usuario';
    },
    totalPermissionsCount() {
      if (!Array.isArray(this.permissions)) return 0;
      return this.permissions.reduce((acc, parent) => {
        if (!Array.isArray(parent?.subModules)) return acc;
        return (
          acc +
          parent.subModules.reduce(
            (subAcc, sub) => subAcc + (sub?.permissions?.length || 0),
            0,
          )
        );
      }, 0);
    },
    selectedPermissionsCount() {
      return (this.claims && this.claims.length) || 0;
    },
  },
};
</script>

<template>
  <el-dialog
    v-model="conf.isVisible"
    class="subscription-account-modal role-admin-modal"
    append-to-body
    :before-close="onCancel"
    :close-on-click-modal="false"
  >
    <!-- Cabecera estandarizada con ícono, títulos y badges -->
    <template #header>
      <div class="student_modal_header">
        <div class="student_modal_header_icon">
          <i class="fas fa-user-shield" />
        </div>
        <div class="student_modal_header_info">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h3 class="student_modal_title">
              {{ modalTitle }}
            </h3>
            <span
              v-if="formData.name"
              class="role_badge_pill pill-primary"
            >
              <i class="fas fa-shield-alt mr-1" /> {{ formData.name }}
            </span>
          </div>
          <p class="student_modal_subtitle">
            {{ modalSubtitle }}
          </p>
        </div>
      </div>
    </template>

    <el-form
      ref="formData"
      v-loading="conf.isLoading"
      :model="formData"
      :rules="rules"
      status-icon
      :label-position="'top'"
    >
      <!-- Sección 1: Información del Rol -->
      <div class="student_form_section">
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-id-badge" /> Información del Rol
          </h4>
        </div>
        <el-row
          class="row-bg"
          :gutter="20"
        >
          <el-col :span="24">
            <el-form-item
              label="Nombre del Rol"
              prop="name"
            >
              <el-input
                v-model="formData.name"
                size="small"
                placeholder="Ingresa el nombre del rol (ej. ADMINISTRADOR, DOCENTE, ASESOR...)"
                :disabled="isUpdate"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- Sección 2: Matriz de Permisos por Módulo -->
      <div class="student_form_section">
        <div class="student_section_header">
          <h4 class="student_section_title">
            <i class="fas fa-key" /> Matriz de Permisos por Módulo
          </h4>
          <div class="role_global_actions">
            <span class="role_total_stats mr-2">
              <i class="fas fa-check-circle text-success mr-1" />
              <strong>{{ selectedPermissionsCount }}</strong> de {{ totalPermissionsCount }} permisos activos
            </span>
            <el-button
              size="small"
              class="btn_role_action"
              @click="onSelectAllPermissions"
            >
              <i class="fas fa-check-double mr-1" /> Marcar Todos
            </el-button>
            <el-button
              size="small"
              class="btn_role_action"
              @click="onDeselectAllPermissions"
            >
              <i class="fas fa-undo mr-1" /> Desmarcar Todos
            </el-button>
          </div>
        </div>

        <el-collapse
          v-model="activeCollapseNames"
          class="role_collapse"
        >
          <el-collapse-item
            v-for="(parentModule, indexModule) in modulesWithPermissions"
            :key="indexModule"
            :name="parentModule.name"
          >
            <template #title>
              <div class="collapse_title_wrap">
                <div class="collapse_title_info">
                  <i class="fas fa-layer-group" />
                  <span>{{ parentModule.name }}</span>
                </div>
                <div class="collapse_title_actions">
                  <span
                    class="badge_module_count"
                    :class="{
                      'is-full': getModuleSelectedCount(parentModule) > 0 && getModuleSelectedCount(parentModule) === getModuleTotalCount(parentModule),
                      'is-partial': getModuleSelectedCount(parentModule) > 0 && getModuleSelectedCount(parentModule) < getModuleTotalCount(parentModule)
                    }"
                  >
                    <i
                      v-if="getModuleSelectedCount(parentModule) > 0 && getModuleSelectedCount(parentModule) === getModuleTotalCount(parentModule)"
                      class="fas fa-check"
                    />
                    {{ getModuleSelectedCount(parentModule) }}/{{ getModuleTotalCount(parentModule) }}
                  </span>
                  <el-button
                    size="small"
                    text
                    type="primary"
                    class="btn_role_action"
                    @click.stop="onSelectAllModulePermissions(parentModule)"
                  >
                    Todos
                  </el-button>
                  <el-button
                    size="small"
                    text
                    type="info"
                    class="btn_role_action"
                    @click.stop="onDeselectAllModulePermissions(parentModule)"
                  >
                    Ninguno
                  </el-button>
                </div>
              </div>
            </template>

            <div class="role_permissions_table">
              <div class="role_table_header">
                <div class="col_submodule">
                  Submódulo / Sección
                </div>
                <div class="col_actions_grid">
                  <span class="col_action_header">Mostrar</span>
                  <span class="col_action_header">Leer</span>
                  <span class="col_action_header">Crear</span>
                  <span class="col_action_header">Actualizar</span>
                  <span class="col_action_header">Eliminar</span>
                </div>
              </div>

              <div
                v-for="(subModule, indexSubmodule) in parentModule.subModules"
                :key="indexSubmodule"
                class="role_table_row"
              >
                <div class="col_submodule">
                  <i class="fas fa-angle-right text-muted mr-1" />
                  {{ subModule.name }}
                </div>
                <div class="col_actions_grid">
                  <el-checkbox-group
                    v-model="claims"
                    class="d-contents"
                  >
                    <div
                      v-for="permission in subModule.permissions"
                      :key="permission.code"
                      class="col_action_cell"
                    >
                      <el-checkbox
                        :value="permission.code"
                        size="small"
                        :title="permission.name || permission.code"
                      />
                    </div>
                  </el-checkbox-group>
                </div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </el-form>

    <!-- Pie del Modal Estandarizado -->
    <template #footer>
      <div class="student_modal_footer">
        <span class="footer_note">
          <i class="fas fa-shield-alt mr-1" />
          {{ isUpdate ? 'Los cambios de permisos se aplicarán a todos los usuarios con este rol' : 'Asigna los permisos necesarios para el nuevo rol de usuario' }}
        </span>
        <div class="footer_actions">
          <el-button
            class="btn_cancel_student_modal"
            @click="onCancel"
          >
            <i class="fas fa-times mr-1" /> Cancelar
          </el-button>
          <el-button
            type="primary"
            :disabled="!onCanSaveOrUpdate()"
            :loading="conf.isLoading"
            class="btn_submit_student_info"
            @click="onSubmitForm('formData')"
          >
            <i class="fas fa-save mr-1" /> {{ isUpdate ? 'Guardar Cambios' : 'Registrar Rol' }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>
